import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { notifications } from '@mantine/notifications';
import { starProduct, unstarProduct } from '@/Api';
import { clientRoutes } from '@/ApiPaths';
import { PENDING_PRODUCT_STAR_STORAGE_KEY } from '@/constants/storageKeys';

export type ProductStarState = {
  starCount: number;
  starredByMe: boolean;
};

type UseProductStarToggleOptions = {
  productId?: string;
  initialStarCount?: number;
  initialStarredByMe?: boolean;
  onStarChange?: (productId: string, state: ProductStarState) => void;
  /** Login redirect path after guest chooses to sign in */
  returnTo?: string;
};

export function useProductStarToggle({
  productId,
  initialStarCount = 0,
  initialStarredByMe = false,
  onStarChange,
  returnTo,
}: UseProductStarToggleOptions) {
  const { t } = useTranslation();
  const session = useSessionContext();
  const navigate = useNavigate();
  const [starCount, setStarCount] = useState(initialStarCount);
  const [starredByMe, setStarredByMe] = useState(initialStarredByMe);
  const [starActionLoading, setStarActionLoading] = useState(false);
  const [starLoginModalOpened, setStarLoginModalOpened] = useState(false);

  useEffect(() => {
    setStarCount(initialStarCount);
    setStarredByMe(initialStarredByMe);
  }, [productId, initialStarCount, initialStarredByMe]);

  const starActionTooltip = useMemo(() => {
    if (session.loading) return t('marketplace.productViewer.star');
    if (!session.doesSessionExist) return t('marketplace.productViewer.starLoginTooltip');
    return starredByMe
      ? t('marketplace.productViewer.unstar')
      : t('marketplace.productViewer.star');
  }, [session, starredByMe, t]);

  const applyStarState = useCallback(
    (next: ProductStarState) => {
      setStarCount(next.starCount);
      setStarredByMe(next.starredByMe);
      if (productId) onStarChange?.(productId, next);
    },
    [onStarChange, productId]
  );

  const handleToggleStar = useCallback(
    async (event?: React.MouseEvent) => {
      event?.stopPropagation();
      event?.preventDefault();
      if (!productId || starActionLoading) return;
      if (session.loading) return;
      if (!session.doesSessionExist) {
        setStarLoginModalOpened(true);
        return;
      }

      setStarActionLoading(true);
      try {
        if (starredByMe) {
          const result = await unstarProduct(productId);
          applyStarState({ starCount: result.starCount, starredByMe: false });
        } else {
          const result = await starProduct(productId);
          applyStarState({ starCount: result.starCount, starredByMe: true });
        }
      } catch (error) {
        console.error(error);
        notifications.show({
          title: t('marketplace.productViewer.starError'),
          message: t('marketplace.productViewer.starErrorHint'),
          color: 'red',
        });
      } finally {
        setStarActionLoading(false);
      }
    },
    [applyStarState, productId, session, starActionLoading, starredByMe, t]
  );

  const confirmStarLogin = useCallback(() => {
    if (!productId) return;
    sessionStorage.setItem(PENDING_PRODUCT_STAR_STORAGE_KEY, productId);
    setStarLoginModalOpened(false);
    const target = returnTo ?? window.location.pathname + window.location.search;
    navigate(`${clientRoutes.login}?returnTo=${encodeURIComponent(target)}`);
  }, [navigate, productId, returnTo]);

  return {
    starCount,
    starredByMe,
    starActionLoading,
    starActionTooltip,
    starLoginModalOpened,
    setStarLoginModalOpened,
    handleToggleStar,
    confirmStarLogin,
  };
}
